import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { ConfirmAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { getNameBusinessTheme } from "../../actions/getters";
import { getMaxModalLayerZIndex } from "../../utils/modalLayer";
import {
  AGENCY_ACCOUNT_LINKING_CONFIRM,
  AGENCY_ACCOUNT_LINKING_CONFIRM_ALERT_BODY,
  AGENCY_ACCOUNT_LINKING_CONFIRM_ALERT_TITLE,
  AGENCY_ACCOUNT_LINKING_SHEET_DESCRIPTION,
  AGENCY_ACCOUNT_LINKING_SHEET_LINKING,
  AGENCY_ACCOUNT_LINKING_SHEET_TITLE,
  BUTTON_CANCEL,
  BUTTON_GO_BACK,
  BUTTON_YES_I_SURE,
} from "../../i18n/keysTranslations";
import {
  AGENCY_LINKING_SOURCE,
  AGENCY_SHEET_LINK_OUTCOME,
  applyLinkResultsToAccounts,
  buildAgencySheetLinkFeedback,
  describeAgencySheetLinkFeedback,
  groupAccountsByIntegration,
  isAgencyAccountSelectable,
  selectableAgencyAccountIds,
} from "../../utils/agencyAccountLinking";
import ClientAccountsTable from "./ClientAccountsTable";

const formatFeedbackLine = ({ line, t }) =>
  line.textKey ? `${line.name}: ${t(line.textKey)}` : line.name;

const ModalAgencyClientAccounts = ({
  modalOpen = false,
  accounts = [],
  onClose = () => {},
  onLinkAccounts = async () => {},
  titleKey = AGENCY_ACCOUNT_LINKING_SHEET_TITLE,
  descriptionKey = AGENCY_ACCOUNT_LINKING_SHEET_DESCRIPTION,
  linkingSource = AGENCY_LINKING_SOURCE.AGENCY_SHEET,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const nameBusiness = dispatch(getNameBusinessTheme()) || "";
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [issue, setIssue] = useState(null);
  const [rows, setRows] = useState(accounts);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    setRows(accounts);
  }, [accounts]);

  useEffect(() => {
    if (!modalOpen) return;
    setSelectedIds(new Set(selectableAgencyAccountIds(accounts)));
  }, [modalOpen, accounts]);
  const tableRows = groupAccountsByIntegration(rows).flatMap(
    (group) => group.accounts
  );
  const selectableIds = selectableAgencyAccountIds(rows);
  const selectedCount = selectableIds.filter((accountID) =>
    selectedIds.has(accountID)
  ).length;
  const issueCopy = describeAgencySheetLinkFeedback(issue);

  const onToggleAccount = (accountID, checked) => {
    if (submittingRef.current) return;
    if (!selectableIds.includes(accountID)) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(accountID);
      else next.delete(accountID);
      return next;
    });
  };

  const onToggleAll = (checked) => {
    if (submittingRef.current) return;
    if (!checked || selectableIds.length === 0) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(selectableIds));
  };

  const onRequestClose = () => {
    if (submittingRef.current) return false;
    onClose();
  };

  const onConfirm = async () => {
    if (submittingRef.current) return;
    const selected = rows.filter(
      (account) =>
        selectedIds.has(account.id) && isAgencyAccountSelectable(account)
    );
    if (selected.length === 0) return;

    submittingRef.current = true;
    let confirmation = null;
    try {
      confirmation = await ConfirmAlert({
        title: t(AGENCY_ACCOUNT_LINKING_CONFIRM_ALERT_TITLE),
        text: t(AGENCY_ACCOUNT_LINKING_CONFIRM_ALERT_BODY, {
          NAME_BUSINESS: nameBusiness,
        }),
        confirmButtonText: t(BUTTON_YES_I_SURE),
        cancelButtonText: t(BUTTON_GO_BACK),
        containerZIndex: Math.max(getMaxModalLayerZIndex() + 20, 10000),
      });
    } catch {
      submittingRef.current = false;
      return;
    }
    if (!confirmation?.isConfirmed) {
      submittingRef.current = false;
      return;
    }

    setSubmitting(true);
    setIssue(null);
    let feedback = null;
    let nextRows = null;
    try {
      const result = await onLinkAccounts({
        accounts: selected,
        source: linkingSource,
        explainResult: true,
      });
      const linkResults = result?.results || [];
      if (linkResults.length > 0) {
        nextRows = applyLinkResultsToAccounts({
          accounts: rows,
          results: linkResults,
        });
      }
      feedback = result?.feedback || null;
    } catch {
      feedback = buildAgencySheetLinkFeedback();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }

    if (nextRows) {
      setRows(nextRows);
      setSelectedIds(new Set(selectableAgencyAccountIds(nextRows)));
      setIssue(null);
      return;
    }

    if (feedback?.outcome !== AGENCY_SHEET_LINK_OUTCOME.ISSUE) {
      onClose();
      return;
    }
    setIssue(feedback);
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onRequestClose}
      title={t(titleKey)}
      description={t(descriptionKey, {
        NAME_BUSINESS: nameBusiness,
      })}
      maxWidth="lg"
      fullWidth
      variantTitle="h6"
      variantDescription="body2"
      sxTitle={{ fontWeight: 700, fontSize: 18, pr: 4 }}
      sxDescription={{
        mt: 1,
        fontSize: 14,
        lineHeight: 1.5,
        color: "text.secondary",
      }}
      ariaLabel={t(titleKey)}
    >
      <Box aria-busy={submitting}>
        <ClientAccountsTable
          accounts={tableRows}
          selectedIds={selectedIds}
          disabled={submitting}
          onToggleAccount={onToggleAccount}
          onToggleAll={onToggleAll}
        />
      </Box>
      <DialogActions
        sx={{
          px: { xs: 3, sm: 5 },
          pt: 1,
          pb: 3,
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.25,
        }}
      >
        {submitting ? (
          <Typography
            role="status"
            aria-live="polite"
            sx={{ fontSize: 13.5, lineHeight: 1.45, color: "text.secondary" }}
          >
            {t(AGENCY_ACCOUNT_LINKING_SHEET_LINKING)}
          </Typography>
        ) : null}
        {issueCopy ? (
          <Box role="alert">
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {t(issueCopy.titleKey)}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 13.5,
                lineHeight: 1.45,
                color: "text.secondary",
              }}
            >
              {t(issueCopy.textKey, issueCopy.textOptions)}
            </Typography>
            {issueCopy.lines.length > 0 ? (
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.25 }}>
                {issueCopy.lines.map((line) => (
                  <Typography
                    component="li"
                    key={line.id}
                    sx={{ fontSize: 13.5, lineHeight: 1.45, mb: 0.5 }}
                  >
                    {formatFeedbackLine({ line, t })}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Box>
        ) : null}
        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            disabled: submitting,
            onClick: onRequestClose,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: AGENCY_ACCOUNT_LINKING_CONFIRM,
            textOptions: { count: selectedCount },
            disabled: selectedCount === 0 || submitting,
            loading: submitting,
            onClick: onConfirm,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalAgencyClientAccounts;
